import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from 'react-bootstrap';
import { Modal } from '../patterns';
import { Orchard, sprayService } from '../../services/sprayService';
import OrchardPicker from './OrchardPicker';

/**
 * Modal that hosts the OrchardPicker for round-based session generation.
 * Reused by single-round, selected-rounds, and all-rounds conversion flows
 * (extend_spray_schema_20260430 spec §2.2 + §2.3 + grilling decision A2).
 *
 * Props:
 *   - `show`         — controlled visibility
 *   - `title`        — modal title (caller picks "Convert Round 3", "Convert
 *                      2 selected rounds", "Convert all 5 rounds", etc.)
 *   - `onConfirm`    — fires with the chosen orchardIds when the user clicks
 *                      Convert. Caller does the actual service call so the
 *                      modal stays decoupled from the single vs bulk
 *                      response-shape difference (409 vs 201).
 *   - `onCancel`     — fires on Close / dismiss.
 *   - `submitting`   — when true the Convert button shows a spinner state
 *                      and is disabled.
 *   - `errorMessage` — optional error text to render above the picker
 *                      (caller surfaces server errors here).
 */
interface Props {
  show: boolean;
  title: string;
  onConfirm: (orchardIds: string[]) => void;
  onCancel: () => void;
  submitting?: boolean;
  errorMessage?: string;
}

const RoundConvertModal: React.FC<Props> = ({
  show, title, onConfirm, onCancel, submitting, errorMessage,
}) => {
  const { t } = useTranslation();
  const [orchards, setOrchards] = useState<Orchard[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show) return;
    setLoading(true);
    sprayService.getOrchards()
      .then(setOrchards)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [show]);

  // Reset selection when the modal closes so the next open starts fresh.
  useEffect(() => {
    if (!show) setSelected([]);
  }, [show]);

  const canConvert = selected.length > 0 && !submitting;

  return (
    <Modal show={show} onHide={onCancel} size="lg" title={title}>
      <Modal.Body>
        {errorMessage && <Alert variant="danger" role="alert">{errorMessage}</Alert>}
        {loading && <p className="text-muted">{t('common.loading')}</p>}
        {!loading && (
          <OrchardPicker
            orchards={orchards}
            selected={selected}
            onChange={setSelected}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          disabled={!canConvert}
          onClick={() => onConfirm(selected)}
        >
          {submitting
            ? t('common.saving')
            : t('spray.programs.convertButton', { count: selected.length })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RoundConvertModal;
